/**
 * Lane/column lookups shared by the Kanban drag handlers: droppable ids,
 * drop-target resolution, and a lane column's filtered + sorted items.
 */

import { useCallback } from 'react';
import type {
  KanbanColumnConfig,
  KanbanDropPreview,
  KanbanFilterDef,
  KanbanGroupOption,
  KanbanItemBase,
  KanbanSortOption,
} from './Kanban.config.types';
import {
  filterKanbanItems,
  findLaneItemsForGroup,
  makeKanbanDroppableId,
  resolveKanbanDropTarget,
  sortKanbanItems,
} from './kanban.utils';

interface UseKanbanLaneLookupOptions<TItem extends KanbanItemBase> {
  features: { swimlanes: boolean; filters: boolean; sorting: boolean };
  itemsById: Map<string, TItem>;
  visibleItems: TItem[];
  groups: Array<{ groupId: string; items: TItem[] }>;
  columnsById: Map<string, KanbanColumnConfig>;
  swimlanesEnabled: boolean;
  groupBy: string | null;
  groupOptions: KanbanGroupOption<TItem>[];
  columnFilters: Record<string, Record<string, string | null>>;
  columnSortBy: Record<string, string | null>;
  filterDefs: KanbanFilterDef<TItem>[];
  sortOptions: KanbanSortOption<TItem>[];
}

export function useKanbanLaneLookup<TItem extends KanbanItemBase>({
  features,
  itemsById,
  visibleItems,
  groups,
  columnsById,
  swimlanesEnabled,
  groupBy,
  groupOptions,
  columnFilters,
  columnSortBy,
  filterDefs,
  sortOptions,
}: UseKanbanLaneLookupOptions<TItem>) {
  const makeDroppableId = useCallback(
    (groupId: string, columnId: string) =>
      makeKanbanDroppableId(columnId, {
        swimlanesEnabled,
        groupId,
      }),
    [swimlanesEnabled]
  );

  const resolveDropTarget = useCallback(
    (overId: string | number | undefined) =>
      resolveKanbanDropTarget(overId, {
        columnsById,
        itemsById,
        swimlanesEnabled,
        groupBy,
        groupOptions: groupOptions as KanbanGroupOption<KanbanItemBase>[],
      }),
    [columnsById, itemsById, swimlanesEnabled, groupBy, groupOptions]
  );

  const getLaneColumnItems = useCallback(
    (laneItems: TItem[], columnId: string) => {
      const columnFilterState = columnFilters[columnId] ?? {};
      const columnSort = columnSortBy[columnId] ?? null;
      let columnItems = laneItems.filter((item) => item.columnId === columnId);
      if (features.filters && Object.keys(columnFilterState).length > 0) {
        columnItems = filterKanbanItems(columnItems, {
          filterDefs,
          activeFilters: columnFilterState,
          columnId,
        });
      }
      if (features.sorting && columnSort) {
        columnItems = sortKanbanItems(columnItems, sortOptions, columnSort);
      }
      return columnItems;
    },
    [
      columnFilters,
      columnSortBy,
      features.filters,
      features.sorting,
      filterDefs,
      sortOptions,
    ]
  );

  const getGroupOption = useCallback(
    () =>
      features.swimlanes && groupBy
        ? groupOptions.find((option) => option.id === groupBy)
        : undefined,
    [features.swimlanes, groupBy, groupOptions]
  );

  /** Preview pointing at the item's current slot (drag source in place). */
  const getOwnSlotPreview = useCallback(
    (item: TItem): KanbanDropPreview => {
      const groupId = getGroupOption()?.getGroupId(item);
      const laneItems = findLaneItemsForGroup(groups, groupId, visibleItems);
      const columnItems = getLaneColumnItems(laneItems, item.columnId);
      return {
        droppableId: makeDroppableId(groupId ?? '__all__', item.columnId),
        columnId: item.columnId,
        groupId,
        index: Math.max(
          0,
          columnItems.findIndex((entry) => entry.id === item.id)
        ),
        overItemId: item.id,
      };
    },
    [getGroupOption, groups, visibleItems, getLaneColumnItems, makeDroppableId]
  );

  return {
    makeDroppableId,
    resolveDropTarget,
    getLaneColumnItems,
    getGroupOption,
    getOwnSlotPreview,
  };
}
